import { Menu, Building2, Settings, Wifi, WifiOff, Loader2, CloudOff, RotateCcw, LogOut } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUIStore } from '@/shared/stores/uiStore'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'
import { useWebSocketStore } from '@/shared/stores/websocketStore'
import { useAuth } from '@/lib/use-auth'
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus'
import { offlineQueueService } from '@/shared/services/offlineQueueService'
import { Button } from '@/shared/components/ui/button'
import { ThemeToggle } from '@/shared/components/ThemeToggle'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { getWorkspaces } from '@/features/workspaces/api/workspacesApi'

export function Header() {
  const { toggleSidebar } = useUIStore()
  const { selectedWorkspace, setSelectedWorkspace, setWorkspaces, workspaces, validateAndFixWorkspace } = useWorkspaceStore()
  const { connectionState, reconnectAttempt } = useWebSocketStore()
  const { isOnline } = useOnlineStatus()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [pendingSyncCount, setPendingSyncCount] = useState(0)

  useEffect(() => {
    // One-time migration: clear invalid workspace data
    const storage = localStorage.getItem('workspace-storage')
    if (storage) {
      try {
        const parsed = JSON.parse(storage)
        const state = parsed.state
        if (state?.selectedWorkspace?.id === '7d8af161-5136-4bed-8ff9-3a8e2d788df9') {
          console.warn('[Migration] Clearing invalid workspace from localStorage')
          localStorage.removeItem('workspace-storage')
        }
      } catch {
        // ignore parse errors
      }
    }
    fetchWorkspaces()
  }, [])

  // Check for pending offline items
  useEffect(() => {
    const checkPendingItems = async () => {
      const stats = await offlineQueueService.getQueueStats()
      setPendingSyncCount(stats.pending + stats.failed)
    }
    
    checkPendingItems()
    const interval = setInterval(checkPendingItems, 5000)
    return () => clearInterval(interval)
  }, [])

  const fetchWorkspaces = async () => {
    setIsLoading(true)
    try {
      const response = await getWorkspaces()
      if (response.success) {
        const workspacesList = response.data.data?.workspaces ?? []
        setWorkspaces(workspacesList)
        // Validate and fix any invalid workspace selection
        validateAndFixWorkspace(workspacesList)
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleWorkspaceChange = (workspaceId: string) => {
    if (workspaceId === 'manage') {
      navigate('/workspaces')
      return
    }
    
    const workspace = workspaces.find(w => w.id === workspaceId)
    if (workspace) {
      setSelectedWorkspace(workspace)
    }
  }

  const hasWorkspaces = workspaces.length > 0

  // WebSocket connection status indicator
  const renderConnectionStatus = () => {
    switch (connectionState) {
      case 'connected':
        return (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Connected to real-time updates">
            <Wifi className="h-3.5 w-3.5 text-green-500" />
            <span className="hidden sm:inline">Connected</span>
          </div>
        )
      case 'connecting':
        return (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Connecting...">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-yellow-500" />
            <span className="hidden sm:inline">Connecting...</span>
          </div>
        )
      case 'reconnecting':
        return (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" title={`Reconnecting (attempt ${reconnectAttempt})`}>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-orange-500" />
            <span className="hidden sm:inline">Reconnecting{reconnectAttempt > 1 ? ` (${reconnectAttempt})` : ''}</span>
          </div>
        )
      case 'disconnected':
      default:
        return (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Disconnected from real-time updates">
            <WifiOff className="h-3.5 w-3.5 text-slate-400" />
            <span className="hidden sm:inline">Offline</span>
          </div>
        )
    }
  }

  // Offline indicator with pending sync badge
  const renderOfflineIndicator = () => {
    if (isOnline && pendingSyncCount === 0) return null

    return (
      <div className="flex items-center gap-2">
        {!isOnline && (
          <div 
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-medium"
            title="Working offline - changes will sync when connection restored"
          >
            <CloudOff className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Offline Mode</span>
          </div>
        )}
        
        {pendingSyncCount > 0 && (
          <div 
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium"
            title={`${pendingSyncCount} items pending sync`}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>{pendingSyncCount} pending</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <header className="flex h-16 items-center justify-between border-b border-border/50 bg-card/80 backdrop-blur-sm px-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        {/* Workspace Selector */}
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold tracking-tight">TimeTracker</h1>
          <div className="h-5 w-px bg-border/60" />
          <Select
            value={selectedWorkspace?.id || ''}
            onValueChange={handleWorkspaceChange}
            disabled={isLoading || !hasWorkspaces}
          >
            <SelectTrigger className="w-[200px] h-9 border-border/60 bg-transparent">
              <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder={isLoading ? 'Loading...' : hasWorkspaces ? 'Select workspace' : 'No workspaces'} />
            </SelectTrigger>
            <SelectContent>
              {workspaces.map((workspace) => (
                <SelectItem key={workspace.id} value={workspace.id}>
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md bg-primary-gradient flex items-center justify-center text-white text-xs font-semibold">
                      {workspace.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="truncate">{workspace.name}</span>
                  </div>
                </SelectItem>
              ))}
              <div className="border-t my-1" />
              <SelectItem value="manage">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Settings className="h-4 w-4" />
                  <span>Manage Workspaces</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {renderOfflineIndicator()}
        {renderConnectionStatus()}
        <ThemeToggle />
        <Button
          variant="ghost"
          size="icon"
          onClick={logout}
          title="Log out"
          className="text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  )
}

export default Header
